import { z } from 'zod'
import { requiredString } from '../../primitives/string'
import { MESSAGES } from '../../utils/messages'

/**
 * Sociedad (entidad legal que posee proyectos y firma por ellos) — fuente única
 * de verdad para la validación del API (nestjs-zod `createZodDto`) y del
 * formulario web (RHF + zodResolver).
 *
 * `name` es único GLOBAL. `dbName`/`dbUser`/`dbPassword` son las credenciales
 * SAP de la sociedad: el API las guarda ENCRIPTADAS (AES-256-GCM) y jamás las
 * devuelve en claro — los listados admin las excluyen por completo y el detalle
 * sale enmascarado. En un update, omitirlas significa "conservar las actuales".
 */

// ObjectId de Mongo serializado (24 hex): el wire actual identifica la compañía
// por ObjectId; no cambiar hasta contrato v2.
const objectIdString = z
  .string({ required_error: MESSAGES.REQUIRED, invalid_type_error: MESSAGES.REQUIRED })
  .regex(/^[a-f0-9]{24}$/i, 'Identificador inválido')

const SocietyInputBaseSchema = z.object({
  name: requiredString()
    .transform((value) => value.trim())
    .pipe(requiredString()),
  company: objectIdString,
  dbName: requiredString(),
  dbUser: requiredString(),
  dbPassword: requiredString(),
  active: z.boolean().optional()
})

export const CreateSocietyInputSchema = SocietyInputBaseSchema

// Credencial omitida O VACÍA = conservar la encriptada actual (el DTO legacy
// aceptaba '' con esa semántica — never break v1): '' se normaliza a undefined
// antes de validar.
const emptyMeansKeep = z.preprocess(
  (value) => (value === '' ? undefined : value),
  requiredString().optional()
)

// Parcial: un PATCH sin credenciales deja las encriptadas existentes intactas
// (semántica de patch — el form de edición solo las envía si se re-escriben).
export const UpdateSocietyInputSchema = SocietyInputBaseSchema.partial().extend({
  dbName: emptyMeansKeep,
  dbUser: emptyMeansKeep,
  dbPassword: emptyMeansKeep
})

export type CreateSocietyInput = z.infer<typeof CreateSocietyInputSchema>
export type UpdateSocietyInput = z.infer<typeof UpdateSocietyInputSchema>
